/**
 * Susunan track lembar log LRLC — kolom apa, urutan apa, skala apa.
 *
 * `LRLC_SHEET` di bawah adalah susunan **bawaannya**, bukan susunan yang
 * dipaksakan. Sampai 16 September 2026 ia memang dipaksakan, dan alasannya
 * ditulis panjang di sini: yang dijawab lembar ini bukan "seperti apa sumur
 * ini" melainkan "apakah penggolongan tadi masuk akal", dan pertanyaan kedua
 * paling mudah dijawab kalau kolomnya selalu berada di tempat yang sama.
 *
 * Alasan itu masih benar, dan tetap menjadi alasan urutan di bawah dipilih
 * seperti itu. Yang tidak benar adalah menyimpulkan darinya bahwa kolomnya
 * tidak boleh disentuh siapa pun: sebuah layar selebar 1.366 piksel memuat
 * enam belas kolom sebagai enam belas pita sempit, dan yang sedang memeriksa
 * satu selang setebal empat meter kerap hanya memerlukan lima di antaranya —
 * berdampingan, bukan terpisah sembilan kolom yang tidak ditanyakannya.
 * Susunannya karena itu kini miliknya: kolom bisa digeser, dibuang, dan
 * ditambah, dan yang tersimpan hanyalah **selisih** terhadap susunan di bawah
 * — lihat `SheetEntry`. Yang dibuang bisa dikembalikan, dan seluruh susunan
 * bawaan selalu satu tombol jauhnya.
 *
 * Enam belas kolom. Enam yang pertama adalah kurva sumurnya apa adanya — GR,
 * resistivitas, DT, densitas dan neutron, VCL, porositas — dan sepuluh sisanya
 * tiap-tiapnya satu tahap hitungan yang bisa dibantah:
 *
 *     RESD / RO             kontras yang dicari — RT terhadap garis airnya
 *     RWA / RW              resistivitas air semu terhadap Rw yang dipakai
 *     F / RWA_RW            faktor formasi, dan perbandingannya
 *     RESD / RT_F           RT terhadap faktor formasi
 *     RT_NORM / RHOB_NORM   silangan ternormalkan, yang tidak membaca Rw
 *     RESERVOIR · PAY       bendera yang lahir dari kelasnya
 *     LRLC class            kelasnya sendiri
 *     RT/RO                 kontrasnya sekali lagi, kali ini sebagai angka
 *     TEST                  apa yang benar-benar keluar ketika dibuka
 *
 * Urutan bawaannya bukan selera. Ia berjalan dari yang paling mudah salah tera
 * ke yang paling sulit: sebuah lembar yang menaruh kolom kelas di sebelah kiri
 * meminta pembacanya mempercayai jawabannya sebelum memperlihatkan satu pun
 * angka yang melahirkannya. Yang memindahkannya sendiri tahu apa yang sedang
 * dilakukannya; yang belum pernah membuka lembar ini mendapat urutan itu.
 *
 * ## Peran, bukan mnemonik
 *
 * Kurva mentah dirujuk lewat peran — `$rt`, `$vsh` — dan bukan lewat namanya.
 * Nama kurva RT di satu lapangan adalah RESD, di lapangan berikutnya LLD, dan
 * susunan yang menuliskan salah satunya akan menjadi kolom kosong pada separuh
 * proyek. Peran keenam kurva yang dibaca penggolong datang dari muatan — layar
 * LRLC-lah yang memilihnya — dan sisanya dicari lewat daftar ejaan yang lazim.
 */

import { orientRange } from "./depthaxis";
import type {
  CrossFill,
  CurveConfig,
  CurveFill,
  CurveStyle,
  TrackConfig,
} from "./tracklayout";
import { toTrackCurve, type TrackCurve, type TrackSpec } from "../components/LogTracks";
import type { CurveData, LrlcSheetPayload } from "../types";

/** Kolom yang digambar sebagai pita warna alih-alih garis. */
export type BandKind = "class" | "reservoir" | "pay" | "test";

export interface SheetCurve {
  /** Mnemonik apa adanya, atau `$peran` yang dicari lebih dulu. */
  name: string;
  color?: string;
  style?: CurveStyle;
  width?: number;
  min?: number;
  max?: number;
  fill?: CurveFill;
  /**
   * Kurva yang tetap ada di kolomnya tetapi tidak digambar.
   *
   * Tersimpan di sini — di dalam **susunan** — dan bukan di `SheetOverrides`,
   * dan itu bukan pilihan sembarang: sebuah kurva yang disembunyikan tidak
   * pernah sampai ke `TrackSpec`, sehingga penyunting yang membacanya dari
   * yang tergambar tidak punya apa pun untuk diperlihatkan kembali. Di dalam
   * susunan ia tetap tercatat, dan matanya bisa dibuka lagi.
   *
   * Berbeda dari membuangnya: yang dibuang hilang dari kolomnya dan kembali
   * lewat "Add curve…" sebagai kurva baru — tanpa warna, skala dan gaya yang
   * sudah disetel orang. Yang dipadamkan menyimpan semuanya.
   */
  hidden?: boolean;
}

export interface SheetTrack {
  id: string;
  title: string;
  units: string;
  flex: number;
  logarithmic?: boolean;
  bands?: BandKind;
  shading?: CrossFill;
  /**
   * Kunci warna arsiran ini di dalam `shading_colors` muatan.
   *
   * Warnanya sendiri milik proyek dan bukan milik berkas ini: yang tertulis
   * pada `shading.color` hanyalah yang dipakai ketika muatannya tidak
   * membawanya — muatan lama, atau uji yang tidak peduli warna.
   */
  shadingKey?: string;
  /**
   * Judulnya sebagai potongan-potongan, tiap potongan menyebut satu kurva.
   *
   * Dipakai kolom yang judulnya memang membaca daftar isinya — "RHOB · NPHI",
   * "GR · SP · CALI" — supaya judulnya menyusut bersama isinya: pada sumur
   * yang tak pernah dilog neutron, kolom densitas berjudul "RHOB", bukan
   * "RHOB · NPHI" di atas satu garis. Sebuah judul yang menyebut kurva yang
   * tidak ada di bawahnya terbaca sebagai kurva yang gagal digambar, dan itu
   * pertanyaan yang salah: yang benar adalah sumur ini tidak punya kurvanya.
   *
   * Ditulis dan tidak diuraikan dari `title`, sebab urutan keduanya tidak
   * selalu sama — kolom pertama berjudul "GR · SP · CALI" dan menggambar
   * CALI lebih dulu supaya GR berada paling atas — dan sebuah judul yang
   * diurai menurut kedudukan akan membuang nama yang salah.
   *
   * Tidak ada berarti judulnya tetap apa adanya: "Normalised" dan "LRLC
   * class" tidak menyebut satu kurva pun, dan tidak ada yang bisa disusutkan.
   */
  titleParts?: { label: string; curve: string }[];
  /**
   * Kurva yang tanpa salah satunya kolom ini tidak punya pertanyaan untuk
   * dijawab — dan karena itu dibuang seluruhnya, bukan digambar separuh.
   *
   * Berbeda dari saringan biasa, yang membuang kolom hanya ketika **tidak
   * satu pun** kurvanya ada. Kolom ternormalkan memperlihatkan silangan: RT
   * yang diskalakan terhadap densitas atau neutron yang diskalakan. Tanpa
   * salah satu pembandingnya yang tersisa hanyalah RT sekali lagi, dalam
   * satuan yang bukan ohm·m — sudah tergambar dua kolom di sebelah kiri, dan
   * di sini justru lebih sulit dibaca.
   */
  needs?: string[];
  /**
   * Kurva pengganti bagi `shading.against` ketika yang disebutnya tidak ada.
   *
   * Satu kolom, satu pertanyaan: kolom ternormalkan menanyakan apakah RT
   * menyilang porositas, dan densitas maupun neutron sama-sama bisa
   * menjawabnya. Sumur yang hanya punya salah satunya tetap mendapat
   * silangannya, alih-alih dua garis tanpa arsiran di antaranya.
   */
  shadingFallback?: string;
  curves: SheetCurve[];
  /**
   * Keterangan sebaris di bawah judulnya — apa yang sebenarnya ditanyakan
   * kolom ini. Dipajang legenda, bukan kepala track: kepala track sudah penuh
   * oleh skala tiap kurva, dan sebuah lembar enam belas kolom yang tiap
   * kolomnya membawa satu kalimat adalah lembar yang tak terbaca.
   */
  asks?: string;
}

/**
 * Ejaan yang lazim tiap kurva yang **tidak** dibaca penggolong.
 *
 * Keenam yang dibaca penggolong tidak ada di sini: namanya datang dari muatan,
 * sebab layar LRLC-lah yang memilihnya, dan menebaknya kembali di sini berarti
 * lembar yang menggambar kurva yang berbeda dari yang barusan dihitung.
 */
export const SHEET_ALIASES: Record<string, string[]> = {
  gr: ["GR", "GRD", "SGR", "CGR", "GRR"],
  sp: ["SP", "SPC"],
  cali: ["CALI", "CAL", "HCAL", "CALS"],
  dt: ["DT", "DTC", "DTCO", "AC", "DTP"],
  /** Resistivitas dangkal — pendamping RT, dan boleh tidak ada. */
  rs: ["RESS", "RS", "ILM", "LLS", "RT10", "RXO", "MSFL"],
};

/** Peran yang namanya datang dari muatan, sebab penggolong yang memilihnya. */
export const SHEET_ROLES = ["rt", "rhob", "nphi", "vsh", "phit", "phie"] as const;

const RED = "#d62828";
const BLUE = "#2a6fd6";
const GREEN = "#2f9e44";
const PURPLE = "#8a3ffc";
const CYAN = "#1098ad";
const BLACK = "var(--ink-2)";

/** Enam belas kolom, dalam urutan yang dibacanya. */
export const LRLC_SHEET: SheetTrack[] = [
  {
    id: "gr", title: "GR · SP · CALI", units: "", flex: 1.1,
    asks: "What kind of rock, before anything is computed",
    titleParts: [
      { label: "GR", curve: "$gr" },
      { label: "SP", curve: "$sp" },
      { label: "CALI", curve: "$cali" },
    ],
    curves: [
      { name: "$cali", color: "#8b939e", style: "dashed", min: 6, max: 16 },
      { name: "$sp", color: BLUE },
      // Diarsir menurut nilainya dan bukan berwarna tunggal: yang dicari mata
      // pada kolom pertama adalah pasir di antara serpih, dan sapuan yang
      // sama pekat pada GR 20 dan GR 140 tidak memperlihatkan itu.
      { name: "$gr", color: "#c26a2a", min: 0, max: 200,
        fill: { from: "min", ramp: "shale", by: "value" } },
    ],
  },
  {
    id: "res", title: "RESD · RESS", units: "ohm·m", flex: 1.05, logarithmic: true,
    asks: "The resistivity as measured — the curve this whole screen doubts",
    titleParts: [
      { label: "RESD", curve: "$rt" },
      { label: "RESS", curve: "$rs" },
    ],
    curves: [
      { name: "$rt", color: RED, min: 0.2, max: 2000 },
      { name: "$rs", color: BLUE, style: "dashed", min: 0.2, max: 2000 },
    ],
  },
  {
    id: "dt", title: "DT", units: "µs/ft", flex: 0.7,
    asks: "Sonic — thin beds show here when resistivity averages them away",
    curves: [{ name: "$dt", color: "#7a9e3a", min: 240, max: 40 }],
  },
  {
    id: "den", title: "RHOB · NPHI", units: "", flex: 1.05,
    asks: "Density and neutron, and where they cross",
    shading: { curve: "$nphi", against: "$rhob", color: "var(--ink-6)" },
    shadingKey: "nphi_rhob",
    titleParts: [
      { label: "RHOB", curve: "$rhob" },
      { label: "NPHI", curve: "$nphi" },
    ],
    curves: [
      { name: "$rhob", color: RED, min: 1.65, max: 2.85 },
      { name: "$nphi", color: BLUE, style: "dashed", min: 0.6, max: 0 },
    ],
  },
  {
    id: "vsh", title: "VCL", units: "v/v", flex: 0.62,
    asks: "Shale volume — the first cutoff a bed has to clear",
    curves: [
      { name: "$vsh", color: "#7a6a52", min: 0, max: 1,
        fill: { from: "max", ramp: "shale", by: "value", reverse: true } },
    ],
  },
  {
    id: "phi", title: "PHIE · PHIT", units: "v/v", flex: 0.95,
    asks: "Porosity — effective inside total; the gap is bound water",
    shading: { curve: "$phit", against: "$phie", color: PURPLE },
    shadingKey: "phit_phie",
    titleParts: [
      { label: "PHIE", curve: "$phie" },
      { label: "PHIT", curve: "$phit" },
    ],
    curves: [
      { name: "$phie", color: CYAN, min: 0.5, max: 0 },
      { name: "$phit", color: PURPLE, style: "dashed", min: 0.5, max: 0 },
    ],
  },
  {
    id: "ro", title: "RESD · RO", units: "ohm·m", flex: 1.1, logarithmic: true,
    asks: "RT against its own water line — everything right of RO is contrast",
    shading: { curve: "$rt", against: "RO", color: RED },
    shadingKey: "rt_ro",
    curves: [
      { name: "$rt", color: RED, min: 0.01, max: 2000 },
      { name: "RO", color: BLUE, style: "dashed", min: 0.01, max: 2000 },
    ],
  },
  {
    id: "rwa", title: "RWA · RW", units: "ohm·m", flex: 1.0, logarithmic: true,
    asks: "Apparent water resistivity against the Rw actually used",
    curves: [
      { name: "RWA", color: BLACK, min: 0.001, max: 100 },
      { name: "RW", color: RED, style: "dashed", min: 0.001, max: 100 },
    ],
  },
  {
    id: "f", title: "F · RWA/RW", units: "", flex: 1.0, logarithmic: true,
    asks: "The formation factor, and how far apparent Rw has drifted from Rw",
    curves: [
      { name: "F", color: CYAN, min: 1, max: 10000 },
      { name: "RWA_RW", color: "#c26a2a", style: "dashed", min: 0.1, max: 1000 },
    ],
  },
  {
    id: "rtf", title: "RESD · RT/F", units: "ohm·m", flex: 1.0, logarithmic: true,
    asks: "RT over the formation factor — the same contrast without Rw in it",
    curves: [
      { name: "$rt", color: RED, min: 0.01, max: 2000 },
      { name: "RT_F", color: BLACK, style: "dashed", min: 0.001, max: 100 },
    ],
  },
  {
    id: "norm", title: "Normalised", units: "v/v", flex: 1.05,
    asks: "The crossover that reads no resistivity scale at all",
    shading: { curve: "RT_NORM", against: "RHOB_NORM", color: GREEN },
    shadingKey: "normalised",
    // Neutron menggantikan densitas ketika sumurnya hanya punya salah satunya,
    // dan kolomnya hilang seluruhnya ketika keduanya tidak ada: RT_NORM
    // sendirian adalah RT sekali lagi, dua kolom di sebelah kirinya.
    shadingFallback: "NPHI_NORM",
    needs: ["RHOB_NORM", "NPHI_NORM"],
    curves: [
      { name: "RT_NORM", color: GREEN, min: 0, max: 1 },
      { name: "RHOB_NORM", color: RED, style: "dashed", min: 0, max: 1 },
      { name: "NPHI_NORM", color: BLUE, style: "dotted", min: 0, max: 1 },
    ],
  },
  {
    id: "reservoir", title: "RES", units: "", flex: 0.34, bands: "reservoir",
    asks: "Net reservoir — rock that can flow, whatever is in it",
    curves: [],
  },
  {
    id: "pay", title: "PAY", units: "", flex: 0.34, bands: "pay",
    asks: "Pay — the same rock, minus the water legs",
    curves: [],
  },
  {
    id: "class", title: "LRLC class", units: "", flex: 0.78, bands: "class",
    asks: "The class itself, after thin blocks were absorbed",
    curves: [],
  },
  {
    // Logaritmik, meski lembar aslinya melukisnya linier pada 0–3.
    //
    // Pada sumbu linier seluruh zona bayar menabrak tepi kanan dan kolomnya
    // menjadi satu balok warna: benar, dan sudah dikatakan tiga kolom di
    // sebelah kiri oleh kolom PAY. Yang hanya bisa dijawab kolom ini adalah
    // **seberapa jauh** di atas ambangnya — RT/RO 1,5 dan RT/RO 60 adalah dua
    // lapisan yang sangat berbeda nasibnya — dan jarak itu hanya ada pada
    // sumbu yang memuat keduanya sekaligus.
    id: "rtro", title: "RT/RO", units: "", flex: 0.85, logarithmic: true,
    asks: "The contrast once more, against its cutoff — how far above, not just whether",
    // Ambangnya digambar di kolomnya sendiri sebagai **kurva**, dan bukan lagi
    // sebagai garis tegak pada satu angka. Tanpa ambangnya kolom ini hanya
    // memperlihatkan sebuah kurva yang naik turun — "seberapa jauh di atas
    // ambang" tidak bisa dibaca tanpa ambangnya ikut tergambar — dan sejak
    // ambang itu boleh berganti di sebuah marker, sebuah garis tegak
    // menggambarkan yang salah: yang terlihat adalah kelas yang berpindah di
    // kedalaman yang garisnya tidak bergerak. RT_RO_CUT tetap lurus pada sumur
    // yang dibaca dengan satu angka, yaitu gambar yang sama seperti dahulu.
    curves: [
      { name: "RT_RO", color: "#c9a227", min: 0.1, max: 1000,
        fill: { from: "min", color: "#c9a227" } },
      { name: "RT_RO_CUT", color: "var(--ink-2)", style: "dashed",
        min: 0.1, max: 1000 },
    ],
  },
  {
    id: "test", title: "TEST", units: "", flex: 0.5, bands: "test",
    asks: "What came up when the interval was opened — the only witness here",
    curves: [],
  },
];

/* ------------------------------------------------- Susunan yang disunting */

/**
 * Satu kolom di dalam susunan: rujukan ke kolom bawaan, atau kolom sendiri.
 *
 * Yang tersimpan adalah selisih, bukan salinan. Kolom yang tidak disentuh
 * isinya tetap sebuah **rujukan** — `{ ref: "ro" }` — sehingga hari ketika
 * `LRLC_SHEET` di atas memperbaiki skala, warna, atau kalimat `asks` sebuah
 * kolom, perbaikan itu sampai juga ke orang yang pernah memindahkan kolomnya.
 * Yang tersimpan di peramban orang hidup lebih lama daripada susunan yang
 * menuliskannya, dan sebuah salinan utuh adalah salinan yang membeku.
 *
 * Kolom menjadi `{ track }` hanya ketika **isinya** berubah — kurva ditambah
 * atau dibuang, judulnya diganti — atau ketika ia memang dibuat sendiri. Warna,
 * skala, lebar dan arsiran tidak membuatnya beku: ketiganya tetap tersimpan
 * sebagai selisih di `SheetOverrides`, sebagaimana sebelumnya.
 *
 * Kolom yang dibuang hilang dari daftar, dan itulah seluruh cara membuangnya
 * tersimpan. Daftar yang tidak ada sama sekali berarti susunan bawaan.
 */
export type SheetEntry = { ref: string } | { track: SheetTrack };

/** Susunan bawaan sebagai daftar rujukan — enam belas kolom, urutan `LRLC_SHEET`. */
export function defaultLayout(): SheetEntry[] {
  return LRLC_SHEET.map((track) => ({ ref: track.id }));
}

/**
 * Membaca susunan yang tersimpan, atau `null` bila tidak ada yang bisa dibaca.
 *
 * Yang di localStorage adalah JSON dari versi aplikasi mana pun yang terakhir
 * dipakai, jadi tidak satu pun medannya boleh dipercaya. Rujukan ke kolom yang
 * sudah tidak ada di `LRLC_SHEET` dibuang di sini, bukan digambar sebagai
 * kolom kosong.
 *
 * Daftar yang berakhir kosong dibaca sebagai "belum ada susunan tersimpan",
 * dan itu disengaja: yang membuang kolom terakhirnya mendapatkan susunan
 * bawaan kembali alih-alih lembar tanpa satu kolom pun. Sebuah lembar kosong
 * memang masih bisa diisi dari rel kanan, tetapi ia terbaca seperti lembar
 * yang rusak, dan tidak ada yang perlu dipertahankan di dalamnya.
 */
export function readLayout(stored: unknown): SheetEntry[] | null {
  if (!Array.isArray(stored)) return null;
  const known = new Set(LRLC_SHEET.map((track) => track.id));
  const entries: SheetEntry[] = [];
  for (const raw of stored) {
    if (!raw || typeof raw !== "object") continue;
    const value = raw as Record<string, unknown>;
    if (typeof value.ref === "string") {
      if (known.has(value.ref)) entries.push({ ref: value.ref });
      continue;
    }
    const track = value.track as SheetTrack | undefined;
    if (track && typeof track.id === "string" && Array.isArray(track.curves)) {
      entries.push({ track });
    }
  }
  return entries.length ? entries : null;
}

/** Susunan sebagai daftar track: rujukan ditemukan, kolom sendiri apa adanya. */
export function layoutTracks(entries: SheetEntry[] | null): SheetTrack[] {
  if (!entries) return LRLC_SHEET;
  const out: SheetTrack[] = [];
  for (const entry of entries) {
    if ("ref" in entry) {
      const found = LRLC_SHEET.find((track) => track.id === entry.ref);
      if (found) out.push(found);
    } else {
      out.push(entry.track);
    }
  }
  return out;
}

/** Benar bila susunannya persis susunan bawaan — urutan, isi, dan jumlahnya. */
export function isDefaultLayout(entries: SheetEntry[] | null): boolean {
  if (!entries) return true;
  return (
    entries.length === LRLC_SHEET.length
    && entries.every((entry, index) =>
      "ref" in entry && entry.ref === LRLC_SHEET[index].id)
  );
}

/** Nama sebuah kolom di dalam susunan, entah ia rujukan atau kolom sendiri. */
function idOf(entry: SheetEntry): string {
  return "ref" in entry ? entry.ref : entry.track.id;
}

/**
 * Kolom bawaan yang tidak ada lagi di susunan — yang masih bisa dipanggil pulang.
 *
 * Dipakai rel kanan untuk menawarkan kolom yang barusan dibuang seseorang,
 * satu per satu. Tanpa daftar ini "buang" adalah pintu satu arah bagi separuh
 * lembar ini: kolom RES, PAY, LRLC class dan TEST tidak menggambar kurva sama
 * sekali — keempatnya pita yang lahir dari hitungan yang sedang berjalan — jadi
 * "New track from curve…" tidak punya apa pun untuk membangunnya kembali. Dan
 * yang **bisa** dibangun kembali dari kurvanya pulang tanpa apa yang
 * menjadikannya kolom itu: arsiran RT terhadap RO, sumbu logaritmik, garis
 * ambang di kolom RT/RO, kalimat yang mengatakan apa yang ditanyakannya.
 *
 * Satu-satunya jalan pulang sebelum ini adalah "Restore the default layout",
 * yang mengembalikan keenam belasnya sekaligus — dan karena itu membuang
 * seluruh susunan yang baru disusun orangnya untuk mendapatkan satu kolom.
 *
 * Kolom yang dibuat sendiri tidak ada di sini: yang dibuangnya tidak punya
 * susunan bawaan untuk dirujuk, dan menawarkan kembali sebuah kolom kosong
 * bernama "Track" sama saja dengan "+ New track" yang sudah ada di sebelahnya.
 */
export function restorableTracks(
  entries: SheetEntry[] | null,
): { id: string; title: string }[] {
  if (!entries) return [];
  const present = new Set(entries.map(idOf));
  return LRLC_SHEET
    .filter((track) => !present.has(track.id))
    .map((track) => ({ id: track.id, title: track.title }));
}

/**
 * Mengembalikan sebuah kolom bawaan ke susunan, pada kedudukan bawaannya.
 *
 * "Kedudukan bawaannya" dibaca terhadap kolom yang **masih ada**, bukan sebagai
 * nomor urut mutlak: kolom kelima `LRLC_SHEET` yang pulang ke tempat kelima
 * sebuah susunan yang sudah dipangkas menjadi tujuh kolom mendarat di antara
 * dua kolom yang tidak ada hubungannya dengannya. Yang dicari adalah kolom
 * pertama yang urutan bawaannya berada **sesudah**nya, dan ia disisipkan tepat
 * sebelum kolom itu — sehingga kolom densitas pulang ke antara DT dan VCL
 * sekalipun keduanya kini kolom kedua dan ketiga.
 *
 * Kolom yang dibuat sendiri dilewati dalam pencarian itu, sebab ia tidak punya
 * kedudukan bawaan untuk dibandingkan; ia tetap di tempatnya, dan kolom yang
 * pulang berdiri di antara tetangga bawaannya.
 *
 * Pulang sebagai **rujukan** dan bukan salinan, sebagaimana `SheetEntry`
 * menjelaskannya: kolom yang dibuang lalu dikembalikan harus tetap ikut ketika
 * skala, warna atau kalimatnya diperbaiki di kemudian hari.
 */
export function restoreEntry(entries: SheetEntry[], id: string): SheetEntry[] {
  const order = LRLC_SHEET.findIndex((track) => track.id === id);
  if (order < 0 || entries.some((entry) => idOf(entry) === id)) return entries;
  const rank = (entry: SheetEntry) =>
    LRLC_SHEET.findIndex((track) => track.id === idOf(entry));
  const at = entries.findIndex((entry) => rank(entry) > order);
  const next = [...entries];
  next.splice(at < 0 ? next.length : at, 0, { ref: id });
  return next;
}

/**
 * Memindahkan satu kolom ke tempat kolom lain — bukan menukar keduanya.
 *
 * Sama seperti `moveTrackTo` pada lembar di menu Log, dan dengan alasan yang
 * sama: menyeret kolom kelima ke tempat pertama menggeser keempat kolom yang
 * dilewatinya satu tempat, sedangkan menukarnya melempar yang pertama ke
 * tempat kelima. Tombol ‹ dan › di rel kanan memang menukar dua tetangga, dan
 * di sana penukaran justru yang dimaksud: satu tekanan, satu langkah.
 */
export function moveEntryTo(
  entries: SheetEntry[],
  id: string,
  targetId: string,
): SheetEntry[] {
  const from = entries.findIndex((entry) => idOf(entry) === id);
  const to = entries.findIndex((entry) => idOf(entry) === targetId);
  if (from < 0 || to < 0 || from === to) return entries;
  const next = [...entries];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/**
 * Susunan berikutnya, dari susunan yang baru disunting rel kanan.
 *
 * Yang dibaca hanya **apa dan di mana** — urutan, kolom yang tersisa, kurva di
 * dalamnya, judul dan satuannya. Warna dan skala tidak dibaca di sini; itu
 * `diffOverrides`, dan memisahkan keduanya berarti sebuah kolom bawaan yang
 * diganti warnanya tetap sebuah rujukan.
 *
 * Kurva yang sudah ada di kolomnya dipertahankan **sebagaimana susunan
 * menuliskannya** — `$rt` tetap `$rt`, bukan nama yang kebetulan dipakai sumur
 * yang sedang terbuka. Itu yang membuat kolom yang disunting tetap mengikuti
 * nama RT tiap sumur ketika sumurnya berganti. Hanya kurva yang baru
 * ditambahkan yang terikat pada mnemonik, sebab hanya mnemonik itu yang
 * disebut orang yang menambahkannya.
 */
export function toLayout(
  next: TrackConfig[],
  previous: SheetEntry[],
  roles: Record<string, string>,
  base: TrackSpec[],
): SheetEntry[] {
  const current = layoutTracks(previous);
  const drawnTracks = new Set(base.map((track) => track.id));

  const converted: SheetEntry[] = next.map((edited) => {
    const source = current.find((track) => track.id === edited.id);
    const spelling = new Map<string, SheetCurve>();
    for (const curve of source?.curves ?? []) {
      const resolved = resolveName(curve.name, roles);
      if (resolved) spelling.set(resolved.toUpperCase(), curve);
    }

    const kept: SheetCurve[] = edited.curves.map((curve) => {
      const known = spelling.get(curve.name.toUpperCase());
      if (known) {
        // Mata yang baru dipadamkan atau dinyalakan adalah satu-satunya hal
        // yang boleh diambil dari penyunting di sini; sisanya — warna, skala,
        // gaya — tetap menjadi selisih di `SheetOverrides`, dan ia selamat
        // dari padamnya sebab `diffOverrides` tidak membuang selisih kurva
        // yang tidak tergambar.
        if (Boolean(known.hidden) === Boolean(curve.hidden)) return known;
        const next = { ...known };
        if (curve.hidden) next.hidden = true;
        else delete next.hidden;
        return next;
      }
      // Kurva yang baru ditambahkan membawa gayanya sendiri ke dalam susunan,
      // bukan ke dalam `SheetOverrides`: sebuah selisih terhadap kurva yang
      // belum ada di pembandingnya tidak punya tempat untuk disimpan, dan
      // gaya putus-putus yang dipilihkan penyunting akan lenyap pada gambar
      // ulang berikutnya.
      return {
        name: curve.name,
        ...(curve.style ? { style: curve.style } : {}),
        ...(curve.hidden ? { hidden: true } : {}),
      };
    });

    // Kurva yang tidak tergambar pada sumur ini tidak ikut dibuang, dengan
    // alasan yang sama seperti kolom yang tidak tergambar di bawah: kolom
    // pertama menyebut CALI, SP dan GR, dan pada sumur yang hanya punya GR
    // kedua yang lain tidak pernah sampai ke penyunting — sehingga menyusun
    // ulang kolomnya dari yang tergambar saja akan membuang CALI dan SP untuk
    // seluruh sumur lain, diam-diam.
    //
    // Yang dipadamkan tidak ikut dilindungi: ia **memang** sampai ke
    // penyunting — dengan matanya tertutup, siap dinyalakan lagi — jadi
    // hilangnya dari `next` berarti tombol buangnya yang barusan ditekan.
    const shown = base.find((track) => track.id === edited.id);
    const drawnCurves = new Set([
      ...(shown?.curves.map((curve) => curve.key ?? curve.name) ?? []),
      ...(source?.curves ?? [])
        .filter((curve) => curve.hidden && resolveName(curve.name, roles))
        .map((curve) => curve.name),
    ]);
    const curves = keepUndrawn(kept, source?.curves ?? [],
                               (curve) => curve.name, drawnCurves);

    // Kembali menjadi rujukan ketika isinya kembali sama dengan kolom
    // bawaannya — dibandingkan terhadap `LRLC_SHEET` dan bukan terhadap
    // keadaan sebelumnya, dengan alasan yang sama seperti `diffOverrides`:
    // sebuah kurva yang dibuang lalu ditambahkan kembali tidak boleh
    // meninggalkan kolom yang membeku pada salinan hari itu.
    //
    // Judulnya dibandingkan dengan judul yang benar-benar **tergambar** dan
    // bukan dengan judul bawaannya: pada sumur tanpa neutron kepala kolom
    // densitas berbunyi "RHOB" — lihat `titleFor` — dan sebuah perbandingan
    // terhadap "RHOB · NPHI" akan membekukan kolom itu, judul susut dan
    // semuanya, untuk seluruh sumur lain begitu ada satu setelan disentuh.
    const builtin = LRLC_SHEET.find((track) => track.id === edited.id);
    if (
      builtin
      && edited.title === (shown?.title ?? builtin.title)
      && edited.units === builtin.units
      && curves.length === builtin.curves.length
      && curves.every((curve, index) =>
        curve.name === builtin.curves[index].name && !curve.hidden)
    ) {
      return { ref: builtin.id };
    }
    // Pita, kalimat `asks`, dan kunci warna arsiran ikut dari kolom asalnya:
    // ketiganya bukan sesuatu yang bisa disebut penyunting track, dan sebuah
    // kolom PAY yang dilebarkan tidak boleh berhenti menjadi kolom PAY.
    const track: SheetTrack = {
      ...(source ?? {}),
      id: edited.id,
      title: edited.title,
      units: edited.units,
      flex: edited.flex,
      // Sumbu mendatar hanya ditulis untuk kolom yang baru dibuat. Pada
      // kolom yang sudah ada ia tersimpan sebagai selisih di
      // `SheetOverrides`, dan menuliskannya di dua tempat berarti dua
      // sumber untuk satu jawaban.
      ...(source ? {} : { logarithmic: edited.scale === "log" }),
      curves,
    } as SheetTrack;
    // Judul yang diketik sendiri mengalahkan judul yang menyusun dirinya dari
    // isi kolomnya: yang menamai kolomnya "Density" memaksudkan "Density", dan
    // sebuah nama yang kembali menjadi "RHOB · NPHI" pada sumur berikutnya
    // adalah suntingan yang tidak pernah mendarat.
    if (edited.title !== (shown?.title ?? builtin?.title ?? source?.title)) {
      delete track.titleParts;
    }
    return { track };
  });

  // Kolom yang ada di susunan tetapi tidak tergambar pada sumur ini kembali ke
  // tempatnya, bukan hilang.
  //
  // `buildLrlcTracks` membuang kolom yang tidak satu pun kurvanya dipunyai
  // sumur yang sedang terbuka — kolom DT pada sumur yang tak pernah dilog
  // sonik — sehingga ia tidak sampai ke penyunting dan karenanya tidak ada di
  // `next`. Menyusun ulang susunan hanya dari `next` berarti mengganti warna
  // satu kurva pada sumur tanpa DT membuang kolom DT untuk **seluruh** sumur
  // lain, diam-diam dan selamanya.
  //
  // `base` yang membedakan keduanya, dan ia harus datang dari luar: kolom yang
  // **tergambar** lalu hilang dari `next` memang baru saja dibuang orangnya,
  // sedangkan kolom yang tidak pernah tergambar tidak pernah punya tombol
  // untuk dibuang. Tanpa perbedaan itu, satu-satunya pilihan adalah salah satu
  // dari dua kesalahan: tombol buang yang tidak membuang apa pun, atau sumur
  // tanpa DT yang membuang kolom DT milik semua sumur.
  return keepUndrawn(converted, previous,
                     (entry) => ("ref" in entry ? entry.ref : entry.track.id),
                     drawnTracks);
}

/**
 * Menyisipkan kembali anggota yang tidak pernah tergambar, pada kedudukannya.
 *
 * Dipakai dua kali — untuk kolom di dalam lembar dan untuk kurva di dalam
 * kolom — sebab keduanya menghadapi hal yang sama: yang tidak tergambar tidak
 * pernah sampai ke penyunting, jadi ketidakhadirannya di sana bukan
 * penghapusan melainkan ketiadaan data pada sumur yang sedang terbuka.
 *
 * Kedudukan semula, bukan ujung daftar: kolom DT yang pulang ke tempat ketiga
 * berada di antara resistivitas dan densitas seperti sebelumnya, sedangkan
 * yang pulang ke ujung kanan menyusun ulang lembar orang setiap kali ia
 * membuka sumur yang tak punya sonik.
 */
function keepUndrawn<T>(
  next: T[],
  previous: T[],
  keyOf: (item: T) => string,
  drawn: Set<string>,
): T[] {
  const out = [...next];
  previous.forEach((item, index) => {
    if (drawn.has(keyOf(item))) return;
    out.splice(Math.min(index, out.length), 0, item);
  });
  return out;
}

/**
 * Nama sungguhan tiap peran, atau kosong bila sumur ini tidak punya kurvanya.
 *
 * Keenam peran penggolong diambil dari muatan apa adanya; sisanya dicari lewat
 * `SHEET_ALIASES`, ejaan pertama yang benar-benar ada yang menang.
 */
export function resolveRoles(payload: LrlcSheetPayload): Record<string, string> {
  const have = new Set(payload.curves.map((curve) => curve.mnemonic.toUpperCase()));
  const out: Record<string, string> = {};
  for (const role of SHEET_ROLES) {
    const name = (payload.names?.[role] ?? "").toUpperCase();
    if (name && have.has(name)) out[role] = name;
  }
  for (const [role, spellings] of Object.entries(SHEET_ALIASES)) {
    const found = spellings.find((name) => have.has(name));
    if (found) out[role] = found;
  }
  return out;
}

/** `$peran` diterjemahkan; nama biasa dipulangkan apa adanya. */
export function resolveName(name: string, roles: Record<string, string>): string {
  return name.startsWith("$") ? (roles[name.slice(1)] ?? "") : name;
}

/**
 * Susunan yang berlaku, ditemukan dengan kurva yang benar-benar ada di sumur ini.
 *
 * `sheet` adalah susunan yang sedang dipakai — bawaan `LRLC_SHEET`, atau yang
 * sudah disunting pembacanya lewat `layoutTracks`. Track yang tidak satu pun
 * kurvanya ada dibuang, sebagaimana lembar di menu Log membuangnya: sebuah
 * kolom DT kosong pada sumur yang tak pernah dilog sonik hanya memakan lebar
 * yang dibutuhkan kolom lainnya. Kolom pita tidak pernah dibuang — ia tidak
 * membaca kurva, dan yang kosong di sana adalah jawaban ("tidak ada pay di
 * sumur ini"), bukan ketiadaan data.
 */
export function buildLrlcTracks(
  payload: LrlcSheetPayload,
  sheet: SheetTrack[] = LRLC_SHEET,
): TrackSpec[] {
  const roles = resolveRoles(payload);
  const byName = new Map<string, CurveData>();
  for (const curve of payload.curves) byName.set(curve.mnemonic.toUpperCase(), curve);

  const out: TrackSpec[] = [];
  for (const spec of sheet) {
    const curves: TrackCurve[] = [];
    spec.curves.forEach((entry) => {
      // Yang dipadamkan tidak pernah menjadi `TrackCurve`, dan karena itu
      // tidak tergambar, tidak dibaca kotak pembaca, dan tidak menambah satu
      // pun angka di kepala kolomnya. Ia tetap ada di susunan — lihat
      // `SheetCurve.hidden` — jadi matanya masih bisa dibuka.
      if (entry.hidden) return;
      const name = resolveName(entry.name, roles);
      const data = name ? byName.get(name) : undefined;
      if (!data) return;
      curves.push(toTrackCurve(data, {
        // Kuncinya nama sebagaimana susunan menuliskannya — `$rt`, `RO` —
        // dan bukan kedudukannya. Setelan warna yang tersimpan menurut
        // kedudukan berpindah ke kurva sebelahnya pada hari sebuah kurva
        // dibuang dari kolomnya, dan sejak susunan ini bisa disunting hari
        // itu adalah hari mana pun.
        key: entry.name,
        color: entry.color ?? data.color,
        style: entry.style ?? "solid",
        width: entry.width,
        logarithmic: spec.logarithmic,
        fill: entry.fill,
        customScale: entry.min !== undefined || entry.max !== undefined,
        ...(entry.min !== undefined ? { min: entry.min } : {}),
        ...(entry.max !== undefined ? { max: entry.max } : {}),
      }));
    });
    // Kolom yang menyebut kurva tetapi tidak satu pun dipunyai sumur ini
    // dibuang, sebagaimana lembar di menu Log membuangnya: kolom DT kosong
    // pada sumur yang tak pernah dilog sonik hanya memakan lebar yang
    // dibutuhkan kolom lain. Kolom yang **tidak menyebut kurva sama sekali**
    // justru dipertahankan: ia kolom pita, atau kolom kosong yang barusan
    // ditambahkan seseorang untuk diisi — dan yang lenyap sedetik setelah
    // ditambahkan terbaca seperti tombol yang tidak bekerja.
    if (!spec.bands && spec.curves.length > 0 && curves.length === 0) continue;

    // Kolom yang kehilangan justru kurva yang melahirkan pertanyaannya dibuang
    // utuh, meski sisanya masih tergambar — lihat `SheetTrack.needs`.
    const drawn = new Set(curves.map((c) => c.name.toUpperCase()));
    if (spec.needs && !spec.needs.some(
      (name) => drawn.has(resolveName(name, roles).toUpperCase()))) continue;

    // Arsiran silang menyebut kurvanya lewat peran juga, dan ia hanya berarti
    // kalau kedua kurvanya memang tergambar: sebuah arsiran terhadap kurva
    // yang tidak ada mengarsir seluruh lebar track dengan satu warna, dan itu
    // terbaca persis seperti lapisan yang tidak ada.
    let shading = spec.shading;
    if (shading) {
      const curve = resolveName(shading.curve, roles);
      let against = resolveName(shading.against, roles);
      // Pembanding cadangan, kalau kolomnya menyebutkan satu — sebuah kolom
      // yang menanyakan silangan tetap bisa menanyakannya lewat kurva yang
      // memang ada di sumur ini.
      if (!drawn.has(against.toUpperCase()) && spec.shadingFallback) {
        against = resolveName(spec.shadingFallback, roles);
      }
      // Warnanya dari proyek bila muatan membawanya; yang di dalam berkas ini
      // hanyalah cadangan bagi muatan yang tidak.
      const chosen = spec.shadingKey
        ? payload.shading_colors?.[spec.shadingKey]
        : undefined;
      shading = drawn.has(curve.toUpperCase()) && drawn.has(against.toUpperCase())
        ? { ...shading, curve, against, color: chosen || shading.color }
        : undefined;
    }

    out.push({
      id: spec.id,
      title: titleFor(spec, drawn, roles),
      units: spec.units,
      flex: spec.flex,
      logarithmic: spec.logarithmic,
      classes: spec.bands === "class",
      bands: spec.bands,
      shading,
      curves,
    });
  }
  return out;
}

/**
 * Judul sebuah kolom, menyebut yang tergambar di dalamnya dan tidak lebih.
 *
 * `drawn` adalah nama kurva yang benar-benar masuk ke kolomnya, dalam huruf
 * besar. Kolom tanpa `titleParts` memulangkan judulnya apa adanya, dan begitu
 * pula kolom yang tidak satu pun bagiannya tergambar: sebuah kepala kolom yang
 * kosong lebih buruk daripada kepala yang menyebut nama yang tidak ada di
 * bawahnya, sebab kolom tanpa nama tidak bisa dicari orang di rel setelannya.
 */
export function titleFor(
  spec: SheetTrack,
  drawn: Set<string>,
  roles: Record<string, string>,
): string {
  if (!spec.titleParts) return spec.title;
  const kept = spec.titleParts.filter(
    (part) => drawn.has(resolveName(part.curve, roles).toUpperCase()));
  return kept.length ? kept.map((part) => part.label).join(" · ") : spec.title;
}

/** Pita yang digambar kolom `kind`, dengan warna kolom itu. */
export function bandsFor(payload: LrlcSheetPayload, kind: BandKind | undefined) {
  if (kind === "class") {
    return payload.bands.map((band) => ({
      top: band.top, base: band.base, code: band.code,
      fill: band.color, label: band.label,
    }));
  }
  if (kind === "reservoir") {
    return payload.reservoir_bands.map((band) => ({
      top: band.top, base: band.base, code: band.code,
      fill: payload.reservoir_color, label: `Net reservoir — ${band.label}`,
    }));
  }
  if (kind === "pay") {
    return payload.pay_bands.map((band) => ({
      top: band.top, base: band.base, code: band.code,
      fill: payload.pay_color, label: `Pay — ${band.label}`,
    }));
  }
  if (kind === "test") {
    return payload.tests.map((test) => ({
      top: test.top, base: test.base, code: test.id,
      fill: test.color,
      label: `${test.kind_label} — ${test.fluid_label}${test.rate ? ` · ${test.rate}` : ""}`,
    }));
  }
  return undefined;
}

/**
 * Apa yang digambar sebuah kolom pita, untuk rel setelannya.
 *
 * Berbeda dari `emptyBandNote`, yang menerangkan sebuah kolom pita yang
 * **kosong** di dalam lembarnya sendiri ("tidak ada pay dengan ambang ini").
 * Yang di sini menerangkan kolomnya kepada yang sedang menyusun lembar: tanpa
 * kalimat ini rel melaporkan keempat kolom pita sebagai "Empty" — sebab tak
 * satu pun memuat kurva — dan sebuah kolom yang mengaku kosong adalah kolom
 * yang dibuang orang berikutnya karena disangka sisa.
 */
export function bandNote(kind: BandKind): string {
  if (kind === "class") return "LRLC class — from the run in progress, not from a curve";
  if (kind === "reservoir") return "Net reservoir flag — from the run, not from a curve";
  if (kind === "pay") return "Pay flag — from the run, not from a curve";
  return "Production tests — from this well's tests, not from a curve";
}

/** Kalimat di dalam kolom pita yang kosong — dan mengapa ia kosong. */
export function emptyBandNote(kind: BandKind | undefined): string {
  if (kind === "test") return "No production test on this well";
  if (kind === "pay") return "No pay flagged with these cutoffs";
  if (kind === "reservoir") return "No net reservoir with these cutoffs";
  return "Nothing classified here";
}

/**
 * Kedalaman teratas dan terbawah yang benar-benar tergambar.
 *
 * Diambil dari kurvanya dan bukan dari `md_range` muatan, dengan alasan yang
 * sama seperti lembar di menu Log: pada acuan ELEV angka terbesar ada di
 * kepala, dan jendela yang selalu menaik menggambar sumurnya terbalik.
 */
export function sheetRange(payload: LrlcSheetPayload): [number, number] | null {
  const edges: number[] = [];
  for (const curve of payload.curves) {
    if (!curve.depths.length) continue;
    edges.push(curve.depths[0], curve.depths[curve.depths.length - 1]);
  }
  if (!edges.length) return null;
  return orientRange(Math.min(...edges), Math.max(...edges),
                     payload.depth_reference);
}


/* ------------------------------------------- Setelan tampilan, bukan susunan */

/**
 * Setelan tampilan yang digeser pengguna, di atas susunan yang berlaku.
 *
 * Dua peta, dan keduanya berkunci **id** — bukan kedudukan. Sebuah setelan
 * yang tersimpan menurut kedudukan pindah ke kolom sebelahnya begitu satu
 * kolom digeser, dibuang, atau ditambahkan ke `LRLC_SHEET` — dan yang
 * tersimpan di peramban orang hidup lebih lama daripada susunan yang
 * menuliskannya.
 *
 * Terpisah dari `SheetEntry` dengan sengaja: yang di sini adalah **cara
 * menggambar**, yang di sana **apa dan di mana**. Kolom bawaan yang diganti
 * warnanya karena itu tetap sebuah rujukan, dan tetap ikut ketika kolomnya
 * diperbaiki di kemudian hari.
 */
export interface SheetOverrides {
  /** Kunci: `trackId`. */
  tracks: Record<string, TrackOverride>;
  /** Kunci: `${trackId}:${curveKey}`. */
  curves: Record<string, Partial<CurveConfig>>;
}

/** Yang boleh berbeda pada sebuah track — semuanya cara menggambar. */
/**
 * `hidden` sengaja tidak ada di sini, dan bukan karena terlupa.
 *
 * Kurva yang disembunyikan tidak pernah sampai ke `TrackSpec` — ia disaring
 * sebelum susunannya dibangun — sehingga sebuah lembar yang menyimpannya
 * sebagai setelan tidak punya jalan pulang: yang menyunting tidak bisa
 * memperlihatkan kembali kurva yang tidak ada di dalam susunan yang dibacanya.
 * Sebuah pintu satu arah lebih buruk daripada tombol yang tidak ada. Sejak
 * susunannya bisa disunting, jalan pulangnya ada di tempat yang benar:
 * membuang kurva dari kolomnya tercatat di `SheetEntry`, dan menambahkannya
 * kembali adalah satu pilihan di daftar "Add curve…".
 */
export interface TrackOverride {
  logarithmic?: boolean;
  /**
   * Lebar kolom terhadap kolom lain.
   *
   * Tersimpan sebagai setelan tampilan dan bukan sebagai bagian susunan,
   * sebab ia tidak mengubah apa yang ditanyakan kolomnya: kolom RESD/RO yang
   * dilebarkan dua kali tetap kolom RESD/RO, dan tetap ikut ketika kolom itu
   * diperbaiki di `LRLC_SHEET`. Sebuah kolom resistivitas yang perlu dua kali
   * lebar untuk terbaca adalah yang paling sering diminta di lembar ini.
   */
  flex?: number;
  spectrum?: TrackConfig["spectrum"];
  shading?: CrossFill | null;
}

export const EMPTY_OVERRIDES: SheetOverrides = { tracks: {}, curves: {} };

/** Membaca yang tersimpan, termasuk bentuk lama yang hanya berisi kurva. */
export function readOverrides(stored: unknown): SheetOverrides {
  if (!stored || typeof stored !== "object") return EMPTY_OVERRIDES;
  const value = stored as Record<string, unknown>;
  // Bentuk lama: sebuah peta datar `${trackId}:${curveKey}` → setelan kurva,
  // ditulis ketika satu-satunya yang bisa disetel adalah kedua ujung skala.
  // Dibaca sebagai bagian `curves`, sebab skala yang sudah disetel orang tidak
  // boleh hilang hanya karena versi berikutnya bisa menyetel lebih banyak.
  if (!("tracks" in value) && !("curves" in value)) {
    return { tracks: {}, curves: renameKeys(value as SheetOverrides["curves"]) };
  }
  return {
    tracks: (value.tracks as SheetOverrides["tracks"]) ?? {},
    curves: renameKeys((value.curves as SheetOverrides["curves"]) ?? {}),
  };
}

/**
 * Kunci kurva yang berupa kedudukan menjadi kunci yang berupa nama.
 *
 * `ro:0` ditulis ketika kunci sebuah kurva adalah nomor urutnya di dalam
 * kolom; sejak kolomnya bisa disunting, kuncinya adalah nama yang ditulis
 * susunan (`ro:$rt`). Yang lama diterjemahkan lewat `LRLC_SHEET` alih-alih
 * dibuang: warna dan skala yang sudah disetel orang tidak boleh hilang hanya
 * karena versi ini menyimpannya dengan kunci yang lebih tahan.
 */
function renameKeys(curves: SheetOverrides["curves"]): SheetOverrides["curves"] {
  const out: SheetOverrides["curves"] = {};
  for (const [key, patch] of Object.entries(curves)) {
    const [trackId, curveKey] = [key.slice(0, key.indexOf(":")), key.slice(key.indexOf(":") + 1)];
    const index = /^\d+$/.test(curveKey) ? Number(curveKey) : -1;
    const named = index >= 0
      ? LRLC_SHEET.find((track) => track.id === trackId)?.curves[index]?.name
      : undefined;
    out[named ? `${trackId}:${named}` : key] = patch;
  }
  return out;
}

/** Menerapkan setelan di atas susunan yang baru dibangun dari muatan. */
export function applyOverrides(tracks: TrackSpec[], overrides: SheetOverrides): TrackSpec[] {
  return tracks.map((track) => {
    const onTrack = overrides.tracks[track.id];
    const curves = track.curves.map((curve) => {
      const patch = overrides.curves[`${track.id}:${curve.key ?? curve.name}`];
      if (!patch) return curve;
      return {
        ...curve,
        ...(patch.color !== undefined ? { color: patch.color } : {}),
        ...(patch.style !== undefined ? { style: patch.style } : {}),
        ...(patch.width !== undefined ? { width: patch.width } : {}),
        ...(patch.min !== undefined ? { min: patch.min } : {}),
        ...(patch.max !== undefined ? { max: patch.max } : {}),
        ...(patch.fill !== undefined ? { fill: patch.fill } : {}),
        customScale: patch.min !== undefined || patch.max !== undefined,
      };
    });
    if (!onTrack) return { ...track, curves };
    return {
      ...track,
      curves,
      ...(onTrack.logarithmic !== undefined ? { logarithmic: onTrack.logarithmic } : {}),
      ...(onTrack.flex !== undefined ? { flex: onTrack.flex } : {}),
      // `null` berarti "arsirannya dimatikan", yang berbeda dari "tidak
      // disetel": tanpa bedanya, mematikan arsiran bawaan sebuah track tidak
      // bisa disimpan sama sekali — ia terbaca sebagai belum pernah disentuh
      // dan hidup lagi pada muat berikutnya.
      ...(onTrack.shading !== undefined
        ? { shading: onTrack.shading ?? undefined }
        : {}),
      ...(onTrack.spectrum !== undefined
        ? { spectrum: resolveSpectrum(onTrack.spectrum, curves) }
        : {}),
    };
  });
}

/**
 * Setelan pita warna menjadi pita yang benar-benar bisa digambar.
 *
 * Kedua ujung jepitannya harus sudah berupa angka ketika sampai ke penggambar
 * — itu kontrak `TrackSpectrum` — dan angka itu datang dari skala kurva yang
 * diwarnai kalau penggunanya tidak menyebutkan sendiri. Dikerjakan persis
 * seperti layar Workstation mengerjakannya, sebab pita yang sama pada dua
 * lembar yang berbeda harus memilih jepitan yang sama.
 */
function resolveSpectrum(
  config: TrackConfig["spectrum"],
  curves: TrackCurve[],
): TrackSpec["spectrum"] {
  if (!config) return undefined;
  const painted = config.curve
    ? curves.find((curve) => curve.name === config.curve) ?? curves[0]
    : curves[0];
  if (!painted) return undefined;
  return {
    curve: painted.name,
    ramp: config.ramp,
    reverse: config.reverse === true,
    lo: config.min ?? painted.min,
    hi: config.max ?? painted.max,
    bars: config.bars === true,
    custom: config.min !== undefined || config.max !== undefined,
  };
}

/**
 * Susunan tergambar sebagai setelan yang bisa disunting rel kanan.
 *
 * Arahnya sekali jalan: `TrackSpec` adalah apa yang tergambar, `TrackConfig`
 * apa yang bisa disetel, dan yang kedua tidak pernah menjadi sumber kebenaran
 * di lembar ini — ia dibangun ulang tiap render dari yang pertama.
 *
 * Kecuali satu hal, dan karena itu `sheet` ikut diminta: kurva yang dipadamkan
 * **tidak** tergambar, jadi ia tidak ada di `TrackSpec` — dan sebuah penyunting
 * yang hanya membaca yang tergambar tidak punya apa pun untuk dinyalakan
 * kembali. Ia disisipkan di sini, pada kedudukannya di dalam susunan, dengan
 * matanya tertutup. Tanpa `sheet` fungsinya persis seperti sebelumnya, dan
 * lembar yang tidak mengenal kurva padam tidak perlu menyebutnya.
 */
export function toTrackConfigs(
  tracks: TrackSpec[],
  sheet: SheetTrack[] = [],
  roles: Record<string, string> = {},
): TrackConfig[] {
  return tracks.map((track) => ({
    id: track.id,
    title: track.title,
    units: track.units,
    flex: track.flex,
    scale: track.logarithmic ? ("log" as const) : ("linear" as const),
    ...(track.classes ? { classes: true } : {}),
    // Kolom pita menyebut dirinya di rel setelan. Tanpa itu keempatnya
    // berbunyi "Empty" di sana — benar menurut kurvanya, dan salah menurut
    // apa yang tergambar di dalamnya.
    ...(track.bands ? { note: bandNote(track.bands as BandKind) } : {}),
    ...(track.shading ? { shading: track.shading } : {}),
    ...(track.spectrum
      ? {
          spectrum: {
            curve: track.spectrum.curve,
            ramp: track.spectrum.ramp,
            reverse: track.spectrum.reverse,
            bars: track.spectrum.bars,
            ...(track.spectrum.custom
              ? { min: track.spectrum.lo, max: track.spectrum.hi }
              : {}),
          },
        }
      : {}),
    curves: withHidden(
      track.curves.map((curve) => ({
        name: curve.name,
        color: curve.color,
        style: curve.style,
        width: curve.width,
        ...(curve.customScale ? { min: curve.min, max: curve.max } : {}),
        ...(curve.fill ? { fill: curve.fill } : {}),
      })),
      sheet.find((spec) => spec.id === track.id),
      roles,
    ),
  }));
}

/**
 * Kurva yang dipadamkan, disisipkan kembali di antara yang tergambar.
 *
 * Pada kedudukannya di dalam susunan dan bukan di ujung daftar: yang membuka
 * rel setelan mencari kurva di tempat ia meninggalkannya, dan sebuah daftar
 * yang menyusun ulang dirinya tiap kali sebuah mata dipadamkan membuat
 * tombol berikutnya berada di tempat yang berbeda daripada sedetik lalu.
 *
 * Yang tidak dipunyai sumur ini dilewati: ia tidak punya nama untuk dipajang,
 * dan sebuah baris kosong bermata tertutup tidak mengatakan apa-apa. Ia tetap
 * tercatat di susunan, dan pulang bersama sumur yang punya kurvanya.
 */
function withHidden(
  drawn: CurveConfig[],
  spec: SheetTrack | undefined,
  roles: Record<string, string>,
): CurveConfig[] {
  if (!spec?.curves.some((curve) => curve.hidden)) return drawn;
  const out = [...drawn];
  spec.curves.forEach((entry, index) => {
    if (!entry.hidden) return;
    const name = resolveName(entry.name, roles);
    if (!name) return;
    out.splice(Math.min(index, out.length), 0, {
      name,
      hidden: true,
      ...(entry.color !== undefined ? { color: entry.color } : {}),
      ...(entry.style !== undefined ? { style: entry.style } : {}),
      ...(entry.width !== undefined ? { width: entry.width } : {}),
      ...(entry.min !== undefined ? { min: entry.min } : {}),
      ...(entry.max !== undefined ? { max: entry.max } : {}),
      ...(entry.fill !== undefined ? { fill: entry.fill } : {}),
    });
  });
  return out;
}

/**
 * Apa yang berubah antara susunan bawaan dan yang baru disunting.
 *
 * Dibandingkan terhadap **bawaannya**, bukan terhadap keadaan sebelumnya. Itu
 * yang membuat sebuah setelan yang dikembalikan ke nilai asalnya benar-benar
 * hilang alih-alih tersimpan sebagai setelan yang kebetulan sama: yang
 * tersimpan hanya selisih, dan lembar tanpa selisih adalah lembar bawaan.
 *
 * Dicocokkan menurut id, bukan menurut kedudukan. Sampai susunan lembar ini
 * terkunci, kedudukan sudah cukup — track ke-i di sini adalah track ke-i di
 * sana — tetapi sebuah kolom yang baru digeser satu tempat akan menerima
 * setelan tetangganya, dan tidak ada apa pun di layar yang mengatakannya.
 * Kolom yang tidak dikenali dilewati, bukan ditebak.
 *
 * `previous` bukan kemewahan. `base` hanyalah yang **tergambar pada sumur ini**
 * — kolom DT hilang pada sumur tanpa sonik, kurva CALI hilang pada sumur tanpa
 * kaliper, kurva yang dipadamkan hilang menurut permintaan — dan yang tidak
 * tergambar tidak punya pembanding untuk dihitung selisihnya. Tanpa `previous`
 * setiap sentuhan di rel kanan akan menulis ulang seluruh setelan dari yang
 * kebetulan tergambar hari itu, dan warna CALI yang disetel orang tahun lalu
 * lenyap pada sumur pertama yang tidak punya kaliper — diam-diam, dan tanpa
 * satu pun cara untuk mengembalikannya.
 */
export function diffOverrides(
  base: TrackSpec[],
  next: TrackConfig[],
  previous: SheetOverrides = EMPTY_OVERRIDES,
): SheetOverrides {
  const tracks: SheetOverrides["tracks"] = {};
  const curves: SheetOverrides["curves"] = {};

  const drawnTracks = new Set(base.map((track) => track.id));
  const drawnCurves = new Set(base.flatMap((track) =>
    track.curves.map((curve) => `${track.id}:${curve.key ?? curve.name}`)));
  for (const [id, patch] of Object.entries(previous.tracks)) {
    if (!drawnTracks.has(id)) tracks[id] = patch;
  }
  for (const [key, patch] of Object.entries(previous.curves)) {
    if (!drawnCurves.has(key)) curves[key] = patch;
  }

  next.forEach((edited) => {
    const original = base.find((track) => track.id === edited.id);
    if (!original) return;

    const onTrack: TrackOverride = {};
    const wasLog = Boolean(original.logarithmic);
    if ((edited.scale === "log") !== wasLog) onTrack.logarithmic = edited.scale === "log";
    if (edited.flex !== original.flex) onTrack.flex = edited.flex;
    if (!sameShading(original.shading, edited.shading)) {
      onTrack.shading = edited.shading ?? null;
    }
    if (JSON.stringify(edited.spectrum ?? null) !== JSON.stringify(spectrumOf(original))) {
      onTrack.spectrum = edited.spectrum;
    }
    if (Object.keys(onTrack).length) tracks[edited.id] = onTrack;

    edited.curves.forEach((curve) => {
      // Dicocokkan menurut nama kurvanya, dengan alasan yang sama seperti
      // track di atas: sebuah kurva yang dibuang dari tengah kolom menggeser
      // kedudukan semua yang di bawahnya, dan warna yang tersimpan akan
      // pindah ke kurva sebelahnya tanpa satu tanda pun.
      const source = original.curves.find((c) => c.name === curve.name);
      if (!source) return;
      const patch: Partial<CurveConfig> = {};
      if (curve.color !== undefined && curve.color !== source.color) patch.color = curve.color;
      if ((curve.style ?? "solid") !== (source.style ?? "solid")) patch.style = curve.style;
      if (curve.width !== source.width) patch.width = curve.width;
      if (curve.min !== undefined && curve.min !== source.min) patch.min = curve.min;
      if (curve.max !== undefined && curve.max !== source.max) patch.max = curve.max;
      if (JSON.stringify(curve.fill ?? null) !== JSON.stringify(source.fill ?? null)) {
        patch.fill = curve.fill;
      }
      for (const field of Object.keys(patch) as (keyof CurveConfig)[]) {
        if (patch[field] === undefined) delete patch[field];
      }
      if (Object.keys(patch).length) curves[`${edited.id}:${source.key ?? source.name}`] = patch;
    });
  });

  return { tracks, curves };
}

function sameShading(a: CrossFill | undefined, b: CrossFill | undefined): boolean {
  if (!a || !b) return !a && !b;
  return a.curve === b.curve && a.against === b.against && a.color === b.color;
}

function spectrumOf(track: TrackSpec): TrackConfig["spectrum"] | null {
  if (!track.spectrum) return null;
  return {
    curve: track.spectrum.curve,
    ramp: track.spectrum.ramp,
    reverse: track.spectrum.reverse,
    bars: track.spectrum.bars,
    ...(track.spectrum.custom ? { min: track.spectrum.lo, max: track.spectrum.hi } : {}),
  };
}
